//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { createContext, useContext, useState } from 'react';

export interface User {
  id: string;
  username: string;
  fullname: string;
  keycloakId?: string;
  firstname?: string | null;
  lastname?: string | null;
}

export interface TeamspaceItem {
  id: string;
  name: string;
  [key: string]: any;
}

export interface TeamspaceContextProps {
  teamspaceName: string | undefined;
  _setTeamspaceName: (name: string | undefined) => void;
  teamspaceId: string | undefined;
  _setTeamspaceId: (id: string | undefined) => void;
  teamspaceSlug: string | undefined;
  _setTeamspaceSlug: (slug: string | undefined) => void;
  _setTenantName: (name: string | undefined) => void;
  tenantName: string | undefined;
  _setTenantId: (id: string | undefined) => void;
  tenantId: string | undefined;
  teamspacesUpdated: boolean | undefined;
  _setTeamspacesUpdated: (updated: boolean) => void;
  userHref: string | undefined;
  _setUserHref: (userHref: string | undefined) => void;
  teamSpaceCreationFailed: boolean | undefined;
  _setTeamSpaceCreationFailed: (failed: boolean | undefined) => void;
  createNewTeamSpace: boolean | undefined;
  _setCreateNewTeamSpace: (failed: boolean | undefined) => void;
  teamspaceUsers: User[];
  _setTeamspaceUsers: (users: User[]) => void;
  _setIncompleteTeamspaceTask: (incompleteTask: string) => void;
  incompleteTeamspaceTask: string | undefined;
  _setTeamspaceList: (teamspaces: TeamspaceItem[]) => void;
  teamspaceList: TeamspaceItem[] | undefined;
}

const TeamspaceContext = createContext<TeamspaceContextProps>({
  teamspaceName: '',
  _setTeamspaceName: () => {},
  teamspaceId: '',
  _setTeamspaceId: () => {},
  teamspaceSlug: '',
  _setTeamspaceSlug: () => {},
  _setTenantName: () => {},
  tenantName: '',
  _setTenantId: () => {},
  tenantId: '',
  teamspacesUpdated: true,
  _setTeamspacesUpdated: () => {},
  userHref: '',
  _setUserHref: () => {},
  teamSpaceCreationFailed: false,
  _setTeamSpaceCreationFailed: () => {},
  createNewTeamSpace: false,
  _setCreateNewTeamSpace: () => {},
  teamspaceUsers: [],
  _setTeamspaceUsers: () => {},
  _setIncompleteTeamspaceTask: () => {},
  incompleteTeamspaceTask: '',
  _setTeamspaceList: () => {},
  teamspaceList: [],
});

interface TeamspaceProviderProps {
  children: React.ReactNode;
}

export const useTeamspaceContext = () => {
  const context = useContext(TeamspaceContext);
  if (context === undefined) {
    throw new Error(
      'useTeamspaceContext must be used within a TeamspaceProvider',
    );
  }
  return context;
};

export const TeamspaceProvider: React.FC<TeamspaceProviderProps> = ({
  children,
}) => {
  const [teamspaceName, _setTeamspaceName] = useState<string | undefined>('');
  const [teamspaceId, _setTeamspaceId] = useState<string | undefined>('');
  const [teamspaceSlug, _setTeamspaceSlug] = useState<string | undefined>('');
  const [tenantId, _setTenantId] = useState<string | undefined>('');
  const [tenantName, _setTenantName] = useState<string | undefined>('');
  const [teamspacesUpdated, _setTeamspacesUpdated] = useState<
    boolean | undefined
  >(false);
  const [userHref, _setUserHref] = useState<string | undefined>('');
  const [teamSpaceCreationFailed, _setTeamSpaceCreationFailed] = useState<
    boolean | undefined
  >(false);
  const [createNewTeamSpace, _setCreateNewTeamSpace] = useState<
    boolean | undefined
  >(false);
  const [teamspaceUsers, _setTeamspaceUsers] = useState<User[]>([]);
  const [incompleteTeamspaceTask, _setIncompleteTeamspaceTask] = useState<
    string | undefined
  >('');
  const [teamspaceList, _setTeamspaceList] = useState<
    TeamspaceItem[] | undefined
  >([]);

  return (
    <TeamspaceContext.Provider
      value={{
        teamspaceName,
        _setTeamspaceName,
        teamspaceId,
        _setTeamspaceId,
        teamspaceSlug,
        _setTeamspaceSlug,
        _setTenantName,
        tenantName,
        _setTenantId,
        tenantId,
        teamspacesUpdated,
        _setTeamspacesUpdated,
        userHref,
        _setUserHref,
        teamSpaceCreationFailed,
        _setTeamSpaceCreationFailed,
        createNewTeamSpace,
        _setCreateNewTeamSpace,
        teamspaceUsers,
        _setTeamspaceUsers,
        _setIncompleteTeamspaceTask,
        incompleteTeamspaceTask,
        _setTeamspaceList,
        teamspaceList,
      }}
    >
      {children}
    </TeamspaceContext.Provider>
  );
};

export { TeamspaceContext };
